/**
 * AlertRulesTable (C.3)
 *
 * Displays all security alert rules with inline enable/disable toggle
 * and edit button for adjusting thresholds/severity.
 */

import { useState } from 'react';
import { Settings, Loader2, ToggleLeft, ToggleRight, Pencil } from 'lucide-react';
import { useAlertRules, useUpdateAlertRule } from '../../hooks/useSecurity';
import { AlertRuleEditModal } from './AlertRuleEditModal';
import type { AlertRule } from '../../types/security.types';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-ems-red/10 text-ems-red',
  high: 'bg-ems-amber/10 text-ems-amber',
  medium: 'bg-ems-amber/10 text-ems-amber',
  low: 'bg-ems-sky/10 text-ems-sky',
};

export function AlertRulesTable() {
  const { data, isLoading } = useAlertRules();
  const updateRule = useUpdateAlertRule();
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);

  const handleToggle = (rule: AlertRule) => {
    updateRule.mutate({
      ruleId: rule.rule_id,
      updates: { is_enabled: !rule.is_enabled },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
      </div>
    );
  }

  const rules = data?.rules || [];

  return (
    <>
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-ems-border">
          <Settings className="w-4 h-4 text-ems-green" />
          <h2 className="text-sm font-semibold text-ems-text-primary">Alert Rules Configuration</h2>
          <span className="text-xs text-ems-text-muted">({rules.length} rules)</span>
        </div>

        {rules.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-ems-text-muted">
            No alert rules configured
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ems-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase">Enabled</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase">Rule Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase">Severity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase">Condition</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.rule_id} className="border-b border-ems-border hover:bg-ems-bg-card-hover">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggle(rule)}
                        className="text-ems-green hover:text-ems-green/80 transition-colors"
                        title={rule.is_enabled ? 'Disable rule' : 'Enable rule'}
                      >
                        {rule.is_enabled ? (
                          <ToggleRight className="w-6 h-6 text-ems-green" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-ems-text-muted" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-ems-text-primary font-medium ${!rule.is_enabled ? 'opacity-50' : ''}`}>
                        {rule.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ems-text-secondary font-mono">
                      {rule.category}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${SEVERITY_COLORS[rule.severity] || 'bg-ems-bg-card-hover text-ems-text-muted'}`}>
                        {rule.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ems-text-secondary max-w-[200px] truncate">
                      {formatCondition(rule)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setEditingRule(rule)}
                        className="flex items-center gap-1 text-xs text-ems-green hover:text-ems-green/80 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingRule && (
        <AlertRuleEditModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
        />
      )}
    </>
  );
}

function formatCondition(rule: AlertRule): string {
  const config = rule.condition_config;
  if (!config || typeof config !== 'object') return rule.condition_type;

  const parts: string[] = [];
  if ('threshold' in config) parts.push(`threshold: ${config.threshold}`);
  if ('window_minutes' in config) parts.push(`window: ${config.window_minutes}m`);
  if ('packet_loss_threshold' in config) parts.push(`loss > ${config.packet_loss_threshold}%`);
  if ('bgp_route_change_pct' in config) parts.push(`route change > ${config.bgp_route_change_pct}%`);
  if ('off_hours_start' in config) parts.push(`hours: ${config.off_hours_start}-${config.off_hours_end}`);
  if ('lookback_days' in config) parts.push(`lookback: ${config.lookback_days}d`);

  return parts.length > 0 ? parts.join(', ') : rule.condition_type;
}
